// Folio single-file core app: companies, documents, logos, banks, per-company sequences, delivery signatures, paid stamp, PDF cleanup
const FOLIO_VERSION = 13;

const PRESET_TEMPLATES = [
  {
    name: 'Midnight Gold',
    colors: {
      headerBg: '#1a1a2e', headerText: '#ffffff', accent: '#c8a96e', docBg: '#ffffff',
      metaBg: '#f9f8f5', docText: '#1a1a2e', footerBg: '#1a1a2e',
      footerText: 'rgba(255,255,255,.55)', border: '#ddd9d0', labelColor: '#888', inputBg: '#fff'
    },
    displayFont: "'Playfair Display', serif",
    bodyFont: "'Outfit', sans-serif"
  },
  {
    name: 'Ocean Slate',
    colors: {
      headerBg: '#1e3a5f', headerText: '#e8f4ff', accent: '#4fc3f7', docBg: '#ffffff',
      metaBg: '#f0f7ff', docText: '#1e3a5f', footerBg: '#1e3a5f',
      footerText: 'rgba(232,244,255,.55)', border: '#c8dff5', labelColor: '#5a7ea0', inputBg: '#fff'
    },
    displayFont: "'Josefin Sans', sans-serif",
    bodyFont: "'Outfit', sans-serif"
  },
  {
    name: 'Mono Minimal',
    colors: {
      headerBg: '#111111', headerText: '#f5f5f5', accent: '#111111', docBg: '#ffffff',
      metaBg: '#fafafa', docText: '#111111', footerBg: '#111111',
      footerText: 'rgba(245,245,245,.5)', border: '#e0e0e0', labelColor: '#888', inputBg: '#fff'
    },
    displayFont: "'DM Mono', monospace",
    bodyFont: "'Outfit', sans-serif"
  },
  {
    name: 'Forest Ink',
    colors: {
      headerBg: '#0f3d2d', headerText: '#f6fff9', accent: '#76d0a4', docBg: '#ffffff',
      metaBg: '#f3fbf6', docText: '#0f2b20', footerBg: '#0f3d2d',
      footerText: 'rgba(246,255,249,.58)', border: '#cfe7d8', labelColor: '#547264', inputBg: '#fff'
    },
    displayFont: "'Cormorant Garamond', serif",
    bodyFont: "'Outfit', sans-serif"
  },
  {
    name: 'Crimson Edit',
    colors: {
      headerBg: '#2b1111', headerText: '#fff4f4', accent: '#ef3b4d', docBg: '#ffffff',
      metaBg: '#fff6f6', docText: '#2b1111', footerBg: '#2b1111',
      footerText: 'rgba(255,244,244,.58)', border: '#efc8cd', labelColor: '#9c5963', inputBg: '#fff'
    },
    displayFont: "'Playfair Display', serif",
    bodyFont: "'Outfit', sans-serif"
  },
  {
    name: 'Sand & Stone',
    colors: {
      headerBg: '#5f4d3e', headerText: '#fff8ef', accent: '#e0b56f', docBg: '#ffffff',
      metaBg: '#fbf6ef', docText: '#3b2d22', footerBg: '#5f4d3e',
      footerText: 'rgba(255,248,239,.58)', border: '#e4d2bd', labelColor: '#856f59', inputBg: '#fff'
    },
    displayFont: "'Libre Baskerville', serif",
    bodyFont: "'Outfit', sans-serif"
  }
];

let companies = [];
let documents = [];
let activeCompanyIdx = 0;
let activeDocId = null;
let _saving = false;
let _applyingRemoteState = false;
let _saveTimer = null;
let _lastRemoteSavedAt = null;
let _newCompanyLogo = null;
let _signatureTarget = 'received';

const $ = id => document.getElementById(id);
const val = id => $(id)?.value || '';
const setVal = (id, v = '') => { const el = $(id); if (el) el.value = v || ''; };
const esc = (s = '') => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');
const pad = n => String(Math.max(0, Number(n) || 0)).padStart(4, '0');
const today = () => new Date().toISOString().slice(0, 10);
const fmtDate = iso => /^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? iso.split('-').reverse().join('/') : (iso || '');
const fmt = n => (Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const clone = obj => JSON.parse(JSON.stringify(obj));
const safeFileName = s => (s || 'document').trim().replace(/[\\/:*?"<>|]+/g, '').replace(/\s+/g, '_').slice(0, 80) || 'document';

function showToast(msg, color) {
  const t = $('toast');
  if (!t) return;
  t.textContent = msg;
  t.style.background = color || 'var(--ui-success)';
  t.style.color = color ? '#fff' : '#0f1117';
  t.classList.add('show');
  setTimeout(() => t.classList.remove('show'), 2200);
}

function showLastSaved() {
  const el = $('lastSavedLabel');
  if (!el) return;
  const d = new Date();
  el.textContent = `Saved ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
  el.style.opacity = '1';
  clearTimeout(showLastSaved._t);
  showLastSaved._t = setTimeout(() => { el.style.opacity = '0'; }, 2500);
}

function getSupabaseClient() {
  const cfg = window.FOLIO_SUPABASE || {};
  if (!cfg.url || !cfg.key || !window.supabase) return null;
  if (!window.__folioSupabaseClient) window.__folioSupabaseClient = window.supabase.createClient(cfg.url, cfg.key);
  return window.__folioSupabaseClient;
}

function injectCoreStyles() {
  if ($('folioCoreSingleStyles')) return;
  const s = document.createElement('style');
  s.id = 'folioCoreSingleStyles';
  s.textContent = `
    .folio-mobile-dock { display:none; }
    .folio-sign-ui { margin-top:4px; }
    .folio-sig-img { width:170px; height:58px; object-fit:contain; object-position:left center; display:block; }
    .folio-sig-date { font-size:.68rem; color:var(--doc-label-color,#888); margin:2px 0 4px; }
    .folio-sign-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:6px; }
    .folio-prepared-sig, .folio-received-sig { min-height:66px; }
    .folio-paid-stamp { border:4px solid #16a34a; color:#16a34a; border-radius:14px; padding:14px 22px; font-weight:900; transform:rotate(-10deg); text-align:center; print-color-adjust:exact; -webkit-print-color-adjust:exact; }
    .company-dashboard-grid { display:grid; grid-template-columns:220px 1fr; gap:14px; }
    .company-bank-card { border:1px solid var(--ui-border); padding:8px; margin-top:8px; border-radius:8px; }
    .company-bank-row { display:flex; gap:6px; margin-top:6px; }
    .color-grid { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
    .color-control { display:flex; align-items:center; justify-content:space-between; gap:8px; border:1px solid var(--ui-border); background:var(--ui-surface2); border-radius:8px; padding:7px 8px; }
    .color-control span { font-size:.68rem; color:var(--ui-text-soft); font-weight:700; letter-spacing:.04em; }
    .color-control input { width:38px; height:28px; padding:0; border:1px solid var(--ui-border); border-radius:6px; background:transparent; cursor:pointer; }
    .save-template-row { display:grid; grid-template-columns:1fr auto; gap:8px; margin-bottom:8px; }
    .save-template-row input { background:var(--ui-surface2); border:1px solid var(--ui-border); color:var(--ui-text); border-radius:8px; padding:8px 10px; font-family:var(--font-ui); min-width:0; }
    .template-chip { color:var(--ui-text); }
    .template-chip-preview { background:#111; overflow:hidden; position:relative; }
    .template-chip-preview::after { content:''; position:absolute; inset:0; background:linear-gradient(135deg, transparent 0 49%, rgba(255,255,255,.12) 50%); }

    @media(max-width:860px) {
      body { padding-bottom:74px; }
      .folio-mobile-dock { position:fixed; left:10px; right:10px; bottom:10px; z-index:220; display:grid; grid-template-columns:repeat(4,1fr); gap:7px; padding:8px; border:1px solid var(--ui-border); border-radius:18px; background:rgba(26,29,39,.94); box-shadow:0 12px 38px rgba(0,0,0,.45); backdrop-filter:blur(14px); }
      .folio-mobile-dock button { border:1px solid var(--ui-border); border-radius:13px; background:var(--ui-surface2); color:var(--ui-text); font-family:var(--font-ui); font-size:.66rem; font-weight:800; padding:8px 4px; }
      .folio-mobile-dock button span { display:block; font-size:1rem; }
      .docs-open .left-panel, .design-open .right-panel { z-index:160!important; }
      .docs-open:before, .design-open:before { content:''; position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:120; }
      .company-dashboard-grid { grid-template-columns:1fr; }
      .company-bank-row { display:grid; grid-template-columns:1fr; }
    }

    body.printing .topbar,
    body.printing .left-panel,
    body.printing .right-panel,
    body.printing .preview-toolbar,
    body.printing .folio-mobile-dock,
    body.printing .modal-overlay,
    body.printing .edit-only,
    body.printing .edit-only-input,
    body.printing .add-row-btn,
    body.printing .del-btn,
    body.printing .col-del-w,
    body.printing .items-tbl th:last-child,
    body.printing .items-tbl td:last-child,
    body.printing #printArea button,
    body.printing #printArea [data-html2canvas-ignore="true"] { display:none!important; }

    body.printing .print-only-text { display:block!important; }
    body.printing .app-shell { display:block!important; height:auto!important; background:#fff!important; }
    body.printing .preview-area { display:block!important; padding:0!important; margin:0!important; background:#fff!important; overflow:visible!important; }
    body.printing #editorWrap,
    body.printing .doc-preview,
    body.printing #printArea,
    body.printing .doc-inner { display:block!important; width:100%!important; max-width:none!important; margin:0!important; box-shadow:none!important; border-radius:0!important; overflow:visible!important; }
    body.printing #printArea input,
    body.printing #printArea textarea { border:0!important; background:transparent!important; box-shadow:none!important; outline:0!important; padding:0!important; color:var(--doc-text,#222)!important; font:inherit!important; resize:none!important; appearance:none!important; -webkit-appearance:none!important; }
    body.printing #fNotes,
    body.printing .notes-area { display:none!important; }
    body.printing .bank-box { border:0!important; background:transparent!important; padding:0!important; min-height:0!important; }
    body.printing .doc-footer-editable { color:var(--doc-footer-text,rgba(255,255,255,.5))!important; text-align:center!important; }
    body.printing #folio-paid-wrap { display:flex!important; visibility:visible!important; opacity:1!important; }
    body.printing #folio-paid-wrap .folio-paid-stamp { display:block!important; visibility:visible!important; opacity:1!important; }

    @media print {
      .topbar,.left-panel,.right-panel,.preview-toolbar,.folio-mobile-dock,.modal-overlay,.edit-only,.edit-only-input,.add-row-btn,.del-btn,.col-del-w,.items-tbl th:last-child,.items-tbl td:last-child,#printArea button { display:none!important; }
      .print-only-text { display:block!important; }
    }
  `;
  document.head.appendChild(s);
}

function defaultCompany(name = 'My Company') {
  const tpl = PRESET_TEMPLATES[companies.length % PRESET_TEMPLATES.length];
  const c = {
    name,
    tagline: '', address: '', phone: '', email: '', website: '', taxNo: '',
    logo: null, logoSize: 60,
    currency: '$', docFooter: 'Thank you for your business.', docTitleOverride: '',
    quotePrefix: 'QUO-', invoicePrefix: 'INV-', dnPrefix: 'DN-',
    sequences: { quotation: { next: 1 }, invoice: { next: 1 }, delivery: { next: 1 } },
    bankAccounts: [], payInst: '', companyFields: [], companySubMode: 'inline',
    theme: clone(tpl.colors), displayFont: tpl.displayFont, bodyFont: tpl.bodyFont,
    stripH: '6', headerPad: '36px 40px', metaPad: '24', itemsPad: '24', footerPad: '14', letterSpacing: '0.04em',
    headerLayout: 'left-logo', savedTemplates: []
  };
  syncBasicToCompanyFields(c);
  return c;
}

function normalizeCompany(c, idx = 0) {
  if (!c) return defaultCompany();
  c.name = c.name || 'Untitled Company';
  c.currency = c.currency || '$';
  c.quotePrefix = c.quotePrefix || 'QUO-';
  c.invoicePrefix = c.invoicePrefix || 'INV-';
  c.dnPrefix = c.dnPrefix || 'DN-';
  c.logoSize = Number(c.logoSize || 60);
  if (!c.theme) c.theme = clone(PRESET_TEMPLATES[0].colors);
  if (!Array.isArray(c.companyFields)) c.companyFields = [];
  if (!Array.isArray(c.bankAccounts)) c.bankAccounts = [];
  if (!c.sequences) c.sequences = {};
  ['quotation', 'invoice', 'delivery'].forEach(type => {
    if (!c.sequences[type]) c.sequences[type] = { next: computeNextNumber(type, idx, c) };
    c.sequences[type].next = Math.max(1, Number(c.sequences[type].next || 1) || 1);
  });
  syncBasicToCompanyFields(c);
  return c;
}

function normalizeAll() {
  if (!Array.isArray(companies)) companies = [];
  if (!Array.isArray(documents)) documents = [];
  if (!companies.length) companies.push(defaultCompany());
  companies.forEach((c, i) => normalizeCompany(c, i));
  activeCompanyIdx = Math.max(0, Math.min(Number(activeCompanyIdx) || 0, companies.length - 1));
  documents.forEach(d => { d.companyIdx = Number(d.companyIdx || 0); migrateDoc(d); });
}

function getCompany() { normalizeAll(); return companies[activeCompanyIdx] || companies[0]; }
function getActiveDoc() { return documents.find(d => d.id === activeDocId); }
function prefixFor(c, type) { return type === 'invoice' ? (c.invoicePrefix || 'INV-') : type === 'delivery' ? (c.dnPrefix || 'DN-') : (c.quotePrefix || 'QUO-'); }
function computeNextNumber(type, idx, c) {
  const prefix = prefixFor(c, type);
  let max = 0;
  documents.filter(d => Number(d.companyIdx) === Number(idx) && d.type === type).forEach(d => {
    const m = String(d.number || '').replace(prefix, '').match(/(\d+)$/);
    if (m) max = Math.max(max, Number(m[1]) || 0);
  });
  return max + 1;
}
function nextDocNumber(type) {
  const c = getCompany();
  normalizeCompany(c, activeCompanyIdx);
  const seq = c.sequences[type] || { next: computeNextNumber(type, activeCompanyIdx, c) };
  const n = Math.max(1, Number(seq.next) || 1);
  seq.next = n + 1;
  c.sequences[type] = seq;
  return prefixFor(c, type) + pad(n);
}
async function getAtomicDocumentNumber(type) { return nextDocNumber(type); }

function syncBasicToCompanyFields(c) {
  if (!c) return;
  if (!Array.isArray(c.companyFields)) c.companyFields = [];
  [['Tagline', 'tagline'], ['Address', 'address', true], ['Phone', 'phone'], ['Email', 'email'], ['Website', 'website'], ['Tax No', 'taxNo']].forEach(([label, key, multi]) => {
    let f = c.companyFields.find(x => String(x.label || '').toLowerCase() === label.toLowerCase());
    if (!f) { f = { label, value: '', isMultiline: !!multi }; c.companyFields.push(f); }
    f.value = c[key] || '';
    if (multi) f.isMultiline = true;
  });
}
function syncFieldsToBasic(c) {
  if (!c || !Array.isArray(c.companyFields)) return;
  const map = { tagline: 'tagline', address: 'address', phone: 'phone', email: 'email', website: 'website', 'tax no': 'taxNo', tax: 'taxNo', 'tax / reg. no.': 'taxNo' };
  c.companyFields.forEach(f => {
    const k = map[String(f.label || '').trim().toLowerCase()];
    if (k) c[k] = f.value || '';
  });
}

function serializeState() { normalizeAll(); return { version: FOLIO_VERSION, companies, documents, activeCompanyIdx, activeDocId, savedAt: new Date().toISOString() }; }
function applyState(payload) {
  if (!payload || typeof payload !== 'object') return;
  companies = Array.isArray(payload.companies) ? payload.companies : [];
  documents = Array.isArray(payload.documents) ? payload.documents : [];
  activeCompanyIdx = Number(payload.activeCompanyIdx || 0) || 0;
  activeDocId = payload.activeDocId || null;
  normalizeAll();
}
async function loadState() {
  const client = getSupabaseClient();
  if (client) {
    try {
      const { data, error } = await client.rpc('folio_get_sync_state');
      if (error) throw error;
      if (data && Object.keys(data).length) { applyState(data); return; }
    } catch (e) {
      console.warn('Folio Supabase load failed', e);
      showToast('Supabase load failed', '#ff6b6b');
    }
  }
  try { const legacy = localStorage.getItem('folio_state'); if (legacy) applyState(JSON.parse(legacy)); } catch {}
}
function persist() {
  if (_applyingRemoteState) return;
  normalizeAll();
  const payload = serializeState();
  localStorage.setItem('folio_state', JSON.stringify(payload));
  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(async () => {
    const client = getSupabaseClient();
    if (!client) return;
    try {
      _saving = true;
      const { error } = await client.rpc('folio_save_sync_state', { p_payload: payload });
      if (error) throw error;
      showLastSaved();
    } catch (e) {
      console.warn('Supabase save failed', e);
      showToast('Supabase save failed', '#ff6b6b');
    } finally { _saving = false; }
  }, 350);
}
function startRealtimeSync() {
  const client = getSupabaseClient();
  if (!client || window.__folioRealtimeStarted) return;
  window.__folioRealtimeStarted = true;
  client.channel('folio-sync-state-global').on('postgres_changes', { event: '*', schema: 'folio', table: 'sync_state', filter: 'id=eq.global' }, payload => {
    const next = payload?.new?.payload;
    if (!next || _saving) return;
    if (next.savedAt && next.savedAt === _lastRemoteSavedAt) return;
    _applyingRemoteState = true;
    try { applyState(next); _lastRemoteSavedAt = next.savedAt || null; renderAfterRemoteState(); }
    finally { _applyingRemoteState = false; }
  }).subscribe();
}
function renderAfterRemoteState() {
  normalizeAll();
  refreshCompanySelector();
  loadCompanyIntoUI(activeCompanyIdx);
  applyTheme();
  renderDocList();
  if (activeDocId && documents.some(d => d.id === activeDocId)) openDoc(activeDocId, false);
  else closeEditor();
  refreshHeader();
  refreshBank();
}

function refreshCompanySelector() {
  const sel = $('companySelector');
  if (!sel) return;
  sel.innerHTML = companies.map((c, i) => `<option value="${i}" ${i === activeCompanyIdx ? 'selected' : ''}>${esc(c.name)}</option>`).join('');
  renderCompanyManagerList();
}
function switchCompany(idx) {
  activeCompanyIdx = Number(idx) || 0;
  activeDocId = null;
  refreshCompanySelector();
  loadCompanyIntoUI(activeCompanyIdx);
  applyTheme();
  renderDocList();
  closeEditor();
  persist();
}
function switchCompanyTo(i) { switchCompany(i); }
function loadCompanyIntoUI(idx) {
  normalizeAll();
  const c = companies[idx];
  if (!c) return;
  setVal('cpName', c.name);
  setVal('pCurrency', c.currency || '$');
  setVal('pPayInst', c.payInst || '');
  setVal('pDocTitle', c.docTitleOverride || '');
  setVal('pQuotePrefix', c.quotePrefix || 'QUO-');
  setVal('pInvoicePrefix', c.invoicePrefix || 'INV-');
  setVal('pDnPrefix', c.dnPrefix || 'DN-');
  setVal('pDisplayFont', c.displayFont);
  setVal('pBodyFont', c.bodyFont);
  setVal('pLetterSpacing', c.letterSpacing);
  renderCompanyFields();
  renderDynamicBankInputs();
  renderSequencePanel();
  refreshHeader();
  refreshBank();
  updateLogoZone();
}
function saveCompanyInfo() {
  const c = getCompany();
  c.name = val('cpName');
  syncFieldsToBasic(c);
  syncBasicToCompanyFields(c);
  refreshCompanySelector();
  refreshHeader();
  persist();
}
function openNewCompanyModal() { openModal('newCompanyModal'); }
function previewNewCompanyLogo(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  const r = new FileReader();
  r.onload = ev => {
    _newCompanyLogo = ev.target.result;
    $('ncLogoZoneContent').innerHTML = `<img src="${_newCompanyLogo}" style="max-height:50px;max-width:100%;object-fit:contain;display:block;margin:0 auto 4px"><div style="font-size:.7rem;color:var(--ui-text-soft)">Click to change</div>`;
  };
  r.readAsDataURL(file);
}
function createCompany() {
  const name = val('ncName').trim();
  if (!name) return showToast('Company name is required', '#ff6b6b');
  const c = defaultCompany(name);
  c.tagline = val('ncTagline');
  c.address = val('ncAddress');
  c.phone = val('ncPhone');
  c.email = val('ncEmail');
  c.website = val('ncWebsite');
  c.taxNo = val('ncTaxNo');
  c.logo = _newCompanyLogo;
  syncBasicToCompanyFields(c);
  companies.push(c);
  activeCompanyIdx = companies.length - 1;
  activeDocId = null;
  ['ncName','ncTagline','ncAddress','ncPhone','ncEmail','ncWebsite','ncTaxNo'].forEach(id => setVal(id, ''));
  _newCompanyLogo = null;
  if ($('ncLogoInput')) $('ncLogoInput').value = '';
  if ($('ncLogoZoneContent')) $('ncLogoZoneContent').innerHTML = '<div style="font-size:1.2rem;opacity:0.4;">🖼</div><div style="font-size:0.75rem;color:var(--ui-text-soft);">Click to upload logo (optional)</div>';
  closeModal('newCompanyModal');
  refreshCompanySelector();
  loadCompanyIntoUI(activeCompanyIdx);
  applyTheme();
  renderDocList();
  closeEditor();
  persist();
  showToast('Company created');
}
function deleteCurrentCompany() {
  if (companies.length <= 1) return showToast('Cannot delete the only company', '#ff6b6b');
  const c = getCompany();
  const docs = documents.filter(d => Number(d.companyIdx) === activeCompanyIdx);
  if (!confirm(`Delete "${c.name}" and ${docs.length} document${docs.length === 1 ? '' : 's'}?`)) return;
  const del = activeCompanyIdx;
  companies.splice(del, 1);
  documents = documents.filter(d => Number(d.companyIdx) !== del).map(d => Number(d.companyIdx) > del ? { ...d, companyIdx: Number(d.companyIdx) - 1 } : d);
  activeCompanyIdx = Math.max(0, del - 1);
  activeDocId = null;
  refreshCompanySelector();
  loadCompanyIntoUI(activeCompanyIdx);
  renderDocList();
  closeEditor();
  persist();
  showToast('Company deleted');
}
function renderCompanyManagerList() {
  const el = $('companyManagerList');
  if (!el) return;
  el.innerHTML = companies.map((c, i) => `<div class="company-item"><span class="company-item-name">${esc(c.name)}</span><div class="company-item-actions"><button class="btn btn-ghost btn-sm" onclick="switchCompany(${i})">Open</button></div></div>`).join('');
}

function migrateDoc(d) {
  if (!d.clientFields) d.clientFields = [{ label: 'Name', value: d.clientName || '' }, { label: 'Address', value: d.clientAddr || '', isMultiline: true }];
  if (!d.detailsFields) d.detailsFields = [{ label: 'Date', value: d.date || today(), isDate: true }, { label: d.type === 'invoice' ? 'Due Date' : 'Valid Until', value: d.due || '', isDate: true }, { label: 'Reference', value: d.ref || '' }];
  if (!Array.isArray(d.items)) d.items = [{ desc: '', qty: 1, unit: '', price: 0 }];
  d.type = d.type || 'quotation';
  if (d.dispatchSignature === undefined) d.dispatchSignature = '';
  if (d.dispatchAt === undefined) d.dispatchAt = '';
  if (d.receivedSignature === undefined) d.receivedSignature = '';
  if (d.receivedAt === undefined) d.receivedAt = '';
  if (d.receivedByName === undefined) d.receivedByName = '';
}
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6); }
async function newDocument() {
  const c = getCompany();
  const doc = { id: uid(), companyIdx: activeCompanyIdx, type: 'quotation', number: await getAtomicDocumentNumber('quotation'), clientName: '', items: [{ desc: '', qty: 1, unit: '', price: 0 }], discount: 0, tax: 0, notes: '', footer: c.docFooter || 'Thank you for your business.' };
  migrateDoc(doc);
  documents.push(doc);
  activeDocId = doc.id;
  persist();
  refreshCompanySelector();
  renderDocList();
  openDoc(doc.id);
}
async function duplicateDoc() {
  const src = getActiveDoc();
  if (!src) return;
  const d = clone(src);
  d.id = uid();
  d.number = await getAtomicDocumentNumber(src.type || 'quotation');
  d.companyIdx = activeCompanyIdx;
  documents.push(d);
  activeDocId = d.id;
  persist();
  renderDocList();
  openDoc(d.id);
}
function openDoc(id, doPersist = true) {
  const d = documents.find(x => x.id === id);
  if (!d) return;
  migrateDoc(d);
  activeDocId = id;
  activeCompanyIdx = Number(d.companyIdx || 0);
  refreshCompanySelector();
  loadCompanyIntoUI(activeCompanyIdx);
  $('emptyState').style.display = 'none';
  $('editorWrap').style.display = 'block';
  setVal('fDiscount', d.discount);
  setVal('fTax', d.tax);
  setVal('fNotes', d.notes);
  setVal('fFooter', d.footer || getCompany().docFooter || 'Thank you for your business.');
  setVal('sigDispatchName', d.sigDispatchName || '');
  setTypeUI(d.type);
  renderClientFields();
  renderDetailsFields();
  renderItems(d.items);
  refreshHeader();
  refreshBank();
  recalc();
  applyDeliveryMode(d.type);
  renderDocList();
  renderDeliverySignature();
  renderPaidStamp();
  autoResizeAll();
  if (doPersist) persist();
}
function closeEditor() { if ($('emptyState')) $('emptyState').style.display = 'flex'; if ($('editorWrap')) $('editorWrap').style.display = 'none'; }
function deleteDoc() { const d = getActiveDoc(); if (!d) return; if (!confirm('Delete this document?')) return; documents = documents.filter(x => x.id !== activeDocId); activeDocId = null; persist(); renderDocList(); closeEditor(); }
async function setDocType(type) {
  const d = getActiveDoc();
  if (!d) return;
  if (d.type !== type) {
    d.type = type;
    d.number = await getAtomicDocumentNumber(type);
    const due = d.detailsFields?.find(f => /due|valid/i.test(f.label));
    if (due) due.label = type === 'invoice' ? 'Due Date' : type === 'delivery' ? 'Dispatch Date' : 'Valid Until';
  }
  setTypeUI(type);
  refreshHeader();
  applyDeliveryMode(type);
  renderDocList();
  persist();
}
function setTypeUI(type) { $('tbtnQ')?.classList.toggle('active', type === 'quotation'); $('tbtnI')?.classList.toggle('active', type === 'invoice'); $('tbtnD')?.classList.toggle('active', type === 'delivery'); }
function overrideDocNumber(v) { const d = getActiveDoc(); if (!d) return; d.number = String(v || '').replace(/^#/, ''); refreshHeader(); renderDocList(); persist(); }
function renderDocList() {
  const el = $('docList');
  if (!el) return;
  const docs = documents.filter(d => Number(d.companyIdx) === activeCompanyIdx).slice().reverse();
  if (!docs.length) { el.innerHTML = '<div style="color:var(--ui-text-soft);font-size:0.78rem;text-align:center;padding:20px 0;">No documents for this company</div>'; return; }
  el.innerHTML = docs.map(d => `<div class="doc-card ${d.id === activeDocId ? 'active' : ''}" onclick="openDoc('${d.id}')"><div class="doc-card-top"><span class="doc-num">#${esc(d.number)}</span><span class="doc-badge ${d.type === 'invoice' ? 'badge-i' : d.type === 'delivery' ? 'badge-d' : 'badge-q'}">${d.type === 'invoice' ? 'INV' : d.type === 'delivery' ? 'DN' : 'QUO'}</span></div><div class="doc-client">${esc(d.clientName || 'No client')}</div></div>`).join('');
}
function applyDeliveryMode(type) {
  const is = type === 'delivery';
  const totals = document.querySelector('.doc-totals');
  const bank = document.querySelector('.doc-notes-bank');
  const sig = $('docSignatureBlock');
  if (totals) totals.style.display = is ? 'none' : '';
  if (sig) sig.style.display = is ? 'grid' : 'none';
  if (bank) { const bankCol = bank.children[1]; if (bankCol) bankCol.style.display = is ? 'none' : ''; }
}

function renderClientFields() { const d = getActiveDoc(); if (!d) return; migrateDoc(d); const el = $('clientFieldsContainer'); el.innerHTML = d.clientFields.map((f, i) => fieldInput('client', i, f)).join(''); syncPrintFields(); }
function renderDetailsFields() { const d = getActiveDoc(); if (!d) return; migrateDoc(d); const el = $('detailsFieldsContainer'); el.innerHTML = d.detailsFields.map((f, i) => fieldInput('details', i, f)).join(''); syncPrintFields(); }
function fieldInput(group, i, f) { const onchange = group === 'client' ? 'updateClientField' : 'updateDetailsField'; return `<div style="display:flex;gap:6px;align-items:center;margin-bottom:6px"><input value="${esc(f.label || '')}" oninput="${onchange}(${i},'label',this.value)" placeholder="Label" style="width:35%"><input ${f.isDate ? 'type="date"' : 'type="text"'} value="${esc(f.value || '')}" oninput="${onchange}(${i},'value',this.value)" placeholder="Value" style="flex:1"><button class="del-btn" onclick="delete${group === 'client' ? 'Client' : 'Details'}Field(${i})">✕</button></div>`; }
function updateClientField(i, k, v) { const d = getActiveDoc(); d.clientFields[i][k] = v; d.clientName = d.clientFields[0]?.value || ''; autoSave(); }
function updateDetailsField(i, k, v) { const d = getActiveDoc(); d.detailsFields[i][k] = v; autoSave(); refreshHeader(); }
function addClientField() { const d = getActiveDoc(); d.clientFields.push({ label: 'Label', value: '' }); renderClientFields(); autoSave(); }
function addDetailsField() { const d = getActiveDoc(); d.detailsFields.push({ label: 'Label', value: '' }); renderDetailsFields(); autoSave(); }
function deleteClientField(i) { const d = getActiveDoc(); d.clientFields.splice(i, 1); renderClientFields(); autoSave(); }
function deleteDetailsField(i) { const d = getActiveDoc(); d.detailsFields.splice(i, 1); renderDetailsFields(); autoSave(); refreshHeader(); }
function renderItems(items) {
  const tb = $('itemsBody');
  if (!tb) return;
  tb.innerHTML = (items || []).map((it, i) => `<tr><td><textarea oninput="updateItem(${i},'desc',this.value);autoResize(this)">${esc(it.desc || '')}</textarea></td><td><input type="number" value="${it.qty || 0}" oninput="updateItem(${i},'qty',this.value)"></td><td><input value="${esc(it.unit || '')}" oninput="updateItem(${i},'unit',this.value)"></td><td><input type="number" value="${it.price || 0}" oninput="updateItem(${i},'price',this.value)"></td><td class="line-total">${cur()}${fmt((Number(it.qty) || 0) * (Number(it.price) || 0))}</td><td><button class="del-btn" onclick="deleteItem(${i})">✕</button></td></tr>`).join('');
  recalc();
}
function updateItem(i, k, v) { const d = getActiveDoc(); if (!d) return; if (k === 'qty' || k === 'price') v = Number(v) || 0; d.items[i][k] = v; recalc(); autoSave(false); }
function addItem() { const d = getActiveDoc(); d.items.push({ desc: '', qty: 1, unit: '', price: 0 }); renderItems(d.items); autoSave(); }
function deleteItem(i) { const d = getActiveDoc(); d.items.splice(i, 1); renderItems(d.items); autoSave(); }
function recalc() {
  const d = getActiveDoc();
  if (!d) return;
  const sub = (d.items || []).reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  d.discount = Number(val('fDiscount')) || 0;
  d.tax = Number(val('fTax')) || 0;
  const after = sub * (1 - d.discount / 100);
  const grand = after * (1 + d.tax / 100);
  if ($('subtotalVal')) $('subtotalVal').textContent = cur() + fmt(sub);
  if ($('grandVal')) $('grandVal').textContent = cur() + fmt(grand);
  document.querySelectorAll('.line-total').forEach((el, i) => { const it = d.items[i]; if (it) el.textContent = cur() + fmt((Number(it.qty) || 0) * (Number(it.price) || 0)); });
}
function autoSave(redrawList = true) {
  const d = getActiveDoc();
  if (!d) return;
  d.discount = Number(val('fDiscount')) || 0;
  d.tax = Number(val('fTax')) || 0;
  d.notes = val('fNotes');
  d.footer = val('fFooter');
  d.sigDispatchName = val('sigDispatchName');
  d.clientName = d.clientFields?.[0]?.value || '';
  syncPrintFields();
  recalc();
  if (redrawList) renderDocList();
  persist();
}
function autoResize(el) { if (!el) return; el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; }
function autoResizeAll() { document.querySelectorAll('textarea').forEach(autoResize); }
function syncPrintFields() {
  const d = getActiveDoc();
  if (!d) return;
  if ($('fNotesPrint')) $('fNotesPrint').textContent = val('fNotes');
  if ($('clientFieldsPrint')) $('clientFieldsPrint').innerHTML = (d.clientFields || []).filter(f => f.value).map(f => f.label ? `<strong>${esc(f.label)}</strong>: ${esc(f.value).replace(/\n/g, '<br>')}` : esc(f.value).replace(/\n/g, '<br>')).join('<br>');
  if ($('detailsFieldsPrint')) $('detailsFieldsPrint').innerHTML = (d.detailsFields || []).filter(f => f.value).map(f => f.label ? `<strong>${esc(f.label)}</strong>: ${esc(f.isDate ? fmtDate(f.value) : f.value)}` : esc(f.value)).join('<br>');
  if ($('sigDispatchNamePrint')) $('sigDispatchNamePrint').textContent = val('sigDispatchName');
}
function cur() { return getCompany().currency || '$'; }

function refreshHeader() {
  const d = getActiveDoc();
  const c = getCompany();
  const type = d?.type || 'quotation';
  const override = val('pDocTitle').trim();
  if ($('dispType')) $('dispType').textContent = override || (type === 'delivery' ? 'DELIVERY NOTE' : type.toUpperCase());
  if ($('dispNum') && $('dispNum') !== document.activeElement) $('dispNum').textContent = d ? '#' + d.number : '';
  const dateFld = d?.detailsFields?.find(f => /date/i.test(f.label) && !/due|valid/i.test(f.label));
  const dueFld = d?.detailsFields?.find(f => /due|valid|dispatch/i.test(f.label));
  if ($('dispDate')) $('dispDate').textContent = dateFld?.value ? 'Date: ' + fmtDate(dateFld.value) : '';
  if ($('dispDue')) $('dispDue').textContent = dueFld?.value ? (type === 'invoice' ? 'Due: ' : type === 'delivery' ? 'Dispatch: ' : 'Valid: ') + fmtDate(dueFld.value) : '';
  if ($('dispCompanyName')) $('dispCompanyName').textContent = c.name || 'Your Company';
  const logo = $('logoWrap');
  if (logo) logo.innerHTML = c.logo ? `<img src="${c.logo}" style="height:${c.logoSize || 60}px;max-height:${c.logoSize || 60}px;width:auto;max-width:100%;object-fit:contain;display:block">` : '';
  const sub = $('dispSub');
  if (sub) {
    syncBasicToCompanyFields(c);
    sub.className = `doc-company-sub company-format-${c.companySubMode || 'inline'}`;
    sub.innerHTML = (c.companyFields || []).filter(f => f.value).map(f => {
      const label = String(f.label || '');
      const isTag = label.toLowerCase() === 'tagline';
      const value = esc(f.value).replace(/\n/g, (c.companySubMode === 'stacked' || c.companySubMode === 'grid') ? '<br>' : ' ');
      return isTag ? `<em class="company-field company-field-tagline">${value}</em>` : c.companySubMode === 'labeled' ? `<span class="company-field"><span class="company-label">${esc(label)}</span><span>${value}</span></span>` : `<span class="company-field">${value}</span>`;
    }).join('');
  }
}
function refreshBank() {
  const c = getCompany();
  if ($('pPayInst')) c.payInst = val('pPayInst');
  const html = (c.bankAccounts || []).map(acc => {
    const rows = (acc.fields || []).filter(f => f.value).map(f => f.label ? `${esc(f.label)}: ${esc(f.value)}` : esc(f.value)).join('<br>');
    return rows ? `${acc.title ? `<strong>${esc(acc.title)}</strong><br>` : ''}${rows}` : '';
  }).filter(Boolean).join('<div style="margin-top:10px"></div>') + (c.payInst ? `<div style="margin-top:10px"><em>${esc(c.payInst)}</em></div>` : '');
  if ($('dispBank')) $('dispBank').innerHTML = html || 'Configure bank details in the Company panel →';
  persist();
}
function saveFinancials() { const c = getCompany(); c.currency = val('pCurrency') || '$'; persist(); recalc(); const d = getActiveDoc(); if (d) renderItems(d.items); }
function applyPageBreak(enabled) { const d = getActiveDoc(); if (d) { d.pageBreak = !!enabled; persist(); } document.querySelector('.doc-totals')?.classList.toggle('doc-page-break-before', !!enabled); if ($('pPageBreak')) $('pPageBreak').checked = !!enabled; }

function renderCompanyFields() {
  const c = getCompany();
  syncBasicToCompanyFields(c);
  const el = $('dynamicCompanyFieldsContainer');
  if (!el) return;
  el.innerHTML = (c.companyFields || []).map((f, i) => `<div style="display:flex;gap:6px;align-items:center;margin-bottom:6px"><input value="${esc(f.label)}" oninput="updateCompanyFieldLabel(${i},this.value)" style="width:35%" placeholder="Label">${f.isMultiline ? `<textarea oninput="updateCompanyFieldValue(${i},this.value)" style="flex:1;min-height:48px">${esc(f.value)}</textarea>` : `<input value="${esc(f.value)}" oninput="updateCompanyFieldValue(${i},this.value)" style="flex:1" placeholder="Value">`}<button class="btn btn-ghost btn-sm" onclick="toggleCompanyFieldMultiline(${i})">▤</button><button class="del-btn" onclick="deleteCompanyField(${i})">✕</button></div>`).join('');
}
function updateCompanyFieldLabel(i, v) { const c = getCompany(); c.companyFields[i].label = v; syncFieldsToBasic(c); refreshHeader(); persist(); }
function updateCompanyFieldValue(i, v) { const c = getCompany(); c.companyFields[i].value = v; syncFieldsToBasic(c); syncBasicToCompanyFields(c); refreshHeader(); persist(); }
function toggleCompanyFieldMultiline(i) { const c = getCompany(); c.companyFields[i].isMultiline = !c.companyFields[i].isMultiline; renderCompanyFields(); persist(); }
function deleteCompanyField(i) { const c = getCompany(); c.companyFields.splice(i, 1); renderCompanyFields(); refreshHeader(); persist(); }
function addCompanyField() { const c = getCompany(); c.companyFields.push({ label: 'Label', value: '' }); renderCompanyFields(); persist(); }
function applyDocTitleOverride() { const c = getCompany(); c.docTitleOverride = val('pDocTitle'); refreshHeader(); persist(); }
function savePrefixes() { const c = getCompany(); c.quotePrefix = val('pQuotePrefix') || 'QUO-'; c.invoicePrefix = val('pInvoicePrefix') || 'INV-'; c.dnPrefix = val('pDnPrefix') || 'DN-'; persist(); renderSequencePanel(); }
function applyPrefixToCurrentDoc() { const d = getActiveDoc(); if (!d) return showToast('Open a document first', '#ff6b6b'); const c = getCompany(); const m = String(d.number || '').match(/(\d+)$/); d.number = prefixFor(c, d.type) + (m ? m[1] : pad(1)); refreshHeader(); renderDocList(); persist(); }
function renderSequencePanel() { const el = $('folioSequenceResetPanel'); if (!el) return; const c = getCompany(); normalizeCompany(c, activeCompanyIdx); el.innerHTML = ['quotation','invoice','delivery'].map(type => `<div class="rp-group"><label>${type === 'delivery' ? 'Delivery Note' : type[0].toUpperCase() + type.slice(1)} next number</label><input type="number" min="1" value="${c.sequences[type].next}" oninput="setCompanySequence('${type}',this.value)"></div>`).join(''); }
function setCompanySequence(type, v) { const c = getCompany(); c.sequences[type] = { next: Math.max(1, Number(v) || 1) }; persist(); }

function renderDynamicBankInputs() {
  const c = getCompany();
  const el = $('dynamicBankContainer');
  if (!el) return;
  el.innerHTML = (c.bankAccounts || []).map((acc, ai) => `<div style="background:rgba(255,255,255,.02);padding:10px;border-radius:6px;border:1px solid var(--ui-border);margin-top:10px"><div style="display:flex;gap:6px;align-items:center;margin-bottom:8px"><input value="${esc(acc.title || '')}" oninput="updateAccountTitle(${ai},this.value)" style="flex:1" placeholder="Account title"><button class="btn btn-danger btn-sm" onclick="deleteBankAccount(${ai})">Delete</button></div>${(acc.fields || []).map((f, fi) => `<div style="display:flex;gap:6px;margin-bottom:6px"><input value="${esc(f.label || '')}" oninput="updateFieldLabel(${ai},${fi},this.value)" style="width:35%" placeholder="Label"><input value="${esc(f.value || '')}" oninput="updateFieldValue(${ai},${fi},this.value)" style="flex:1" placeholder="Value"><button class="del-btn" onclick="deleteField(${ai},${fi})">✕</button></div>`).join('')}<button class="btn btn-ghost btn-sm" onclick="addFieldToAccount(${ai})">+ Add Field</button></div>`).join('') || '<div style="font-size:.75rem;color:var(--ui-text-soft);padding:10px 0">No bank accounts added yet.</div>';
  refreshBank();
}
function addNewBankAccount() { const c = getCompany(); c.bankAccounts.push({ title: 'New Account', fields: [{ label: 'Bank Name', value: '' }, { label: 'Account Name', value: '' }, { label: 'Account Number', value: '' }] }); renderDynamicBankInputs(); persist(); }
function updateAccountTitle(i, v) { getCompany().bankAccounts[i].title = v; refreshBank(); persist(); }
function deleteBankAccount(i) { getCompany().bankAccounts.splice(i, 1); renderDynamicBankInputs(); persist(); }
function addFieldToAccount(i) { getCompany().bankAccounts[i].fields.push({ label: 'Label', value: '' }); renderDynamicBankInputs(); persist(); }
function updateFieldLabel(ai, fi, v) { getCompany().bankAccounts[ai].fields[fi].label = v; refreshBank(); persist(); }
function updateFieldValue(ai, fi, v) { getCompany().bankAccounts[ai].fields[fi].value = v; refreshBank(); persist(); }
function deleteField(ai, fi) { getCompany().bankAccounts[ai].fields.splice(fi, 1); renderDynamicBankInputs(); persist(); }

function showTab(id) { document.querySelectorAll('.rtab').forEach(b => b.classList.remove('active')); document.querySelectorAll('.rtab-content').forEach(t => t.classList.remove('active')); $(id)?.classList.add('active'); document.querySelector(`[onclick="showTab('${id}')"]`)?.classList.add('active'); }
function openModal(id) { $(id)?.classList.add('open'); $(id)?.classList.add('show'); }
function closeModal(id) { $(id)?.classList.remove('open'); $(id)?.classList.remove('show'); }
function togglePanel(which) { document.body.classList.toggle(which === 'docs' ? 'docs-open' : 'design-open'); }
function applyTheme() {
  const c = getCompany();
  const root = document.documentElement;
  Object.entries(c.theme || {}).forEach(([k, v]) => root.style.setProperty('--doc-' + k.replace(/[A-Z]/g, m => '-' + m.toLowerCase()), v));
  root.style.setProperty('--doc-header-bg', c.theme?.headerBg || '#111');
  root.style.setProperty('--doc-header-text', c.theme?.headerText || '#fff');
  root.style.setProperty('--doc-accent', c.theme?.accent || '#111');
  root.style.setProperty('--doc-bg', c.theme?.docBg || '#fff');
  root.style.setProperty('--doc-text-color', c.theme?.docText || '#111');
  root.style.setProperty('--doc-footer-bg', c.theme?.footerBg || '#111');
  root.style.setProperty('--doc-footer-text', c.theme?.footerText || 'rgba(255,255,255,.5)');
  root.style.setProperty('--doc-border-color', c.theme?.border || '#ddd');
}
function templatePreviewStyle(colors = {}) {
  const a = colors.headerBg || '#111';
  const b = colors.accent || '#777';
  const c = colors.docBg || '#fff';
  return `background:linear-gradient(135deg, ${a} 0 50%, ${b} 50% 72%, ${c} 72% 100%)`;
}
function colorLabel(k) {
  return ({ headerBg:'Header', headerText:'Header Text', accent:'Accent', docBg:'Page', metaBg:'Meta', docText:'Text', footerBg:'Footer', footerText:'Footer Text', border:'Borders', labelColor:'Labels', inputBg:'Inputs' })[k] || k;
}
function buildColorControls() {
  const el = $('colorGrid');
  if (!el) return;
  const c = getCompany();
  el.innerHTML = Object.keys(c.theme || {}).map(k => `<label class="color-control"><span>${esc(colorLabel(k))}</span><input type="color" value="${esc(c.theme[k])}" oninput="getCompany().theme['${k}']=this.value;applyTheme();buildTemplateGrid();persist()"></label>`).join('');
}
function buildTemplateGrid() {
  const el = $('presetGrid');
  if (!el) return;
  const c = getCompany();
  el.innerHTML = PRESET_TEMPLATES.map((t, i) => {
    const active = JSON.stringify(c.theme || {}) === JSON.stringify(t.colors || {});
    return `<button type="button" class="template-chip ${active ? 'active-tpl' : ''}" onclick="applyPreset(${i})"><div class="template-chip-preview" style="${templatePreviewStyle(t.colors)}"></div>${esc(t.name)}</button>`;
  }).join('');
}
function applyPreset(i) {
  const c = getCompany();
  const t = PRESET_TEMPLATES[i];
  if (!t) return;
  c.theme = clone(t.colors);
  c.displayFont = t.displayFont;
  c.bodyFont = t.bodyFont;
  applyTheme();
  loadCompanyIntoUI(activeCompanyIdx);
  loadDesignControls();
  persist();
}
function ensureDesignSaveRow() {
  if ($('saveTemplateName')) return;
  const grid = $('savedTemplateGrid');
  if (!grid) return;
  const btn = Array.from(document.querySelectorAll('button')).find(b => (b.getAttribute('onclick') || '').includes('saveCurrentAsTemplate'));
  if (!btn) return;
  const row = document.createElement('div');
  row.className = 'save-template-row';
  row.innerHTML = `<input id="saveTemplateName" placeholder="Template name..."><button type="button" class="btn btn-warning btn-sm" onclick="saveCurrentAsTemplate()">Save</button>`;
  btn.style.display = 'none';
  grid.insertAdjacentElement('afterend', row);
}
function snapshotDesign() {
  const c = getCompany();
  return {
    theme: clone(c.theme || {}), displayFont: c.displayFont, bodyFont: c.bodyFont,
    stripH: c.stripH, headerPad: c.headerPad, metaPad: c.metaPad, itemsPad: c.itemsPad, footerPad: c.footerPad,
    letterSpacing: c.letterSpacing, headerMinH: c.headerMinH, companySubMode: c.companySubMode,
    logoPos: c.logoPos, companyPos: c.companyPos, stampPos: c.stampPos
  };
}
function applyDesignSnapshot(tpl) {
  if (!tpl) return;
  const c = getCompany();
  Object.assign(c, clone(tpl));
  applyTheme();
  loadCompanyIntoUI(activeCompanyIdx);
  loadDesignControls();
  persist();
}
function saveCurrentAsTemplate() {
  const c = getCompany();
  if (!Array.isArray(c.savedTemplates)) c.savedTemplates = [];
  const name = (val('saveTemplateName') || `Design ${c.savedTemplates.length + 1}`).trim();
  c.savedTemplates.push({ name, ...snapshotDesign() });
  setVal('saveTemplateName', '');
  buildSavedTemplateList();
  persist();
  showToast('Design template saved');
}
function buildSavedTemplateList() {
  ensureDesignSaveRow();
  const el = $('savedTemplateGrid');
  if (!el) return;
  const c = getCompany();
  const list = c.savedTemplates || [];
  el.innerHTML = list.length ? list.map((t, i) => `<button type="button" class="template-chip" onclick="applySavedTemplate(${i})"><div class="template-chip-preview" style="${templatePreviewStyle(t.theme)}"></div>${esc(t.name || 'Saved Design')}</button>`).join('') : `<div style="grid-column:1/-1;color:var(--ui-text-soft);font-size:.72rem;padding:6px 0;">No saved templates yet.</div>`;
}
function applySavedTemplate(i) { applyDesignSnapshot((getCompany().savedTemplates || [])[i]); }
function deleteSavedTemplate(i) { const c = getCompany(); (c.savedTemplates || []).splice(i, 1); buildSavedTemplateList(); persist(); }
function buildHeaderLayoutPresets() {
  const el = $('headerLayoutPresetGrid');
  if (!el) return;
  const layouts = [
    { key:'left-logo', name:'Logo Left', logo:0, company:3, stamp:2 },
    { key:'split-top', name:'Split Top', logo:0, company:1, stamp:2 },
    { key:'stack-left', name:'Stack Left', logo:0, company:3, stamp:5 }
  ];
  const c = getCompany();
  el.innerHTML = layouts.map(l => `<button type="button" class="template-chip header-layout-chip ${c.headerLayout === l.key ? 'active-tpl' : ''}" onclick="applyHeaderLayout('${l.key}')"><div class="header-layout-mini"><span class="hl-logo" style="grid-area:${Math.floor(l.logo/3)+1}/${(l.logo%3)+1}"></span><span class="hl-company" style="grid-area:${Math.floor(l.company/3)+1}/${(l.company%3)+1}"></span><span class="hl-stamp" style="grid-area:${Math.floor(l.stamp/3)+1}/${(l.stamp%3)+1}"></span></div>${esc(l.name)}</button>`).join('');
}
function applyHeaderLayout(key) {
  const c = getCompany();
  const layouts = { 'left-logo':{logoPos:0,companyPos:3,stampPos:2}, 'split-top':{logoPos:0,companyPos:1,stampPos:2}, 'stack-left':{logoPos:0,companyPos:3,stampPos:5} };
  Object.assign(c, layouts[key] || layouts['left-logo']);
  c.headerLayout = key;
  buildHeaderLayoutPresets();
  buildPosGrids();
  applyLayout();
}
function posToVars(pos) {
  const p = Math.max(0, Math.min(8, Number(pos) || 0));
  const col = (p % 3) + 1;
  const row = Math.floor(p / 3) + 1;
  const justify = col === 1 ? 'start' : col === 2 ? 'center' : 'end';
  const align = row === 1 ? 'start' : row === 2 ? 'center' : 'end';
  const textAlign = justify === 'start' ? 'left' : justify === 'end' ? 'right' : 'center';
  return { col, row, justify, align, textAlign };
}
function buildOnePosGrid(id, key) {
  const el = $(id);
  if (!el) return;
  const c = getCompany();
  const current = Number(c[`${key}Pos`] ?? (key === 'stamp' ? 2 : key === 'company' ? 3 : 0));
  el.innerHTML = Array.from({ length: 9 }, (_, i) => `<button type="button" class="pos-cell ${i === current ? 'active' : ''}" onclick="setHeaderPos('${key}',${i})">${i + 1}</button>`).join('');
}
function buildPosGrids() { buildOnePosGrid('logoPosGrid', 'logo'); buildOnePosGrid('companyPosGrid', 'company'); buildOnePosGrid('stampPosGrid', 'stamp'); }
function setHeaderPos(key, pos) { getCompany()[`${key}Pos`] = Number(pos) || 0; buildPosGrids(); applyLayout(); }
function loadPosGridsFromCompany() { buildPosGrids(); }
function loadColorControls() { buildColorControls(); }
function loadDesignControls() {
  const c = getCompany();
  setVal('pDisplayFont', c.displayFont || "'Playfair Display', serif");
  setVal('pBodyFont', c.bodyFont || "'Outfit', sans-serif");
  setVal('pLetterSpacing', c.letterSpacing || '0.04em');
  setVal('pCompanySubMode', c.companySubMode || 'inline');
  const nums = [['pHeaderPadSlider','lbHeaderPadV',parseInt(c.headerPad,10)||36],['pMetaPadSlider','lbMetaPadV',Number(c.metaPad)||24],['pItemsPadSlider','lbItemsPadV',Number(c.itemsPad)||24],['pFooterPadSlider','lbFooterPadV',Number(c.footerPad)||14],['pStripH','lbStripHV',Number(c.stripH)||6],['pHeaderMinH','lbHeaderMinH',Number(c.headerMinH)||120]];
  nums.forEach(([id, label, value]) => { setVal(id, value); if ($(label)) $(label).textContent = value; });
  if ($('fontPreviewDisplay')) $('fontPreviewDisplay').style.fontFamily = c.displayFont;
  if ($('fontPreviewBody')) $('fontPreviewBody').style.fontFamily = c.bodyFont;
  buildTemplateGrid(); buildSavedTemplateList(); buildColorControls(); buildHeaderLayoutPresets(); buildPosGrids();
}
function applyFonts() {
  const c = getCompany();
  c.displayFont = val('pDisplayFont') || c.displayFont;
  c.bodyFont = val('pBodyFont') || c.bodyFont;
  applyLayout();
}
function applyLayout() {
  const c = getCompany();
  c.headerPad = `${Number(val('pHeaderPadSlider')) || parseInt(c.headerPad,10) || 36}px 40px`;
  c.metaPad = Number(val('pMetaPadSlider')) || Number(c.metaPad) || 24;
  c.itemsPad = Number(val('pItemsPadSlider')) || Number(c.itemsPad) || 24;
  c.footerPad = Number(val('pFooterPadSlider')) || Number(c.footerPad) || 14;
  c.stripH = Number(val('pStripH')) || Number(c.stripH) || 0;
  c.headerMinH = Number(val('pHeaderMinH')) || Number(c.headerMinH) || 120;
  c.letterSpacing = val('pLetterSpacing') || c.letterSpacing || '0.04em';
  c.companySubMode = val('pCompanySubMode') || c.companySubMode || 'inline';
  c.displayFont = val('pDisplayFont') || c.displayFont;
  c.bodyFont = val('pBodyFont') || c.bodyFont;
  const root = document.documentElement;
  root.style.setProperty('--doc-display-font', c.displayFont);
  root.style.setProperty('--doc-body-font', c.bodyFont);
  root.style.setProperty('--doc-header-padding', c.headerPad);
  root.style.setProperty('--doc-meta-padding', `${c.metaPad}px`);
  root.style.setProperty('--doc-items-padding', `${c.itemsPad}px`);
  root.style.setProperty('--doc-footer-padding', `${c.footerPad}px`);
  root.style.setProperty('--doc-strip-height', `${c.stripH}px`);
  root.style.setProperty('--doc-header-letter-spacing', c.letterSpacing);
  root.style.setProperty('--doc-header-min-height', `${c.headerMinH}px`);
  [['logo', c.logoPos ?? 0], ['company', c.companyPos ?? 3], ['stamp', c.stampPos ?? 2]].forEach(([key, pos]) => {
    const v = posToVars(pos);
    root.style.setProperty(`--doc-${key}-col`, v.col);
    root.style.setProperty(`--doc-${key}-row`, v.row);
    root.style.setProperty(`--doc-${key}-justify`, v.justify);
    root.style.setProperty(`--doc-${key}-align`, v.align);
    root.style.setProperty(`--doc-${key}-text-align`, v.textAlign);
  });
  if ($('fontPreviewDisplay')) $('fontPreviewDisplay').style.fontFamily = c.displayFont;
  if ($('fontPreviewBody')) $('fontPreviewBody').style.fontFamily = c.bodyFont;
  refreshHeader(); applyTheme(); persist();
}
function uploadLogo(e) { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = ev => { const c = getCompany(); c.logo = ev.target.result; updateLogoZone(); refreshHeader(); persist(); }; r.readAsDataURL(file); }
function removeLogo() { const c = getCompany(); c.logo = null; updateLogoZone(); refreshHeader(); persist(); }
function resizeLogo(v) { const c = getCompany(); c.logoSize = Number(v) || 60; if ($('lbLogoSize')) $('lbLogoSize').textContent = c.logoSize; refreshHeader(); persist(); }
function updateLogoZone() {
  const c = getCompany();
  if (c.logo) {
    if ($('logoZoneContent')) $('logoZoneContent').innerHTML = `<img src="${c.logo}" style="max-height:50px;max-width:100%;object-fit:contain;display:block;margin:0 auto 4px"><div class="logo-hint">Click to change</div>`;
    if ($('removeLogoBtn')) $('removeLogoBtn').style.display = 'block';
    if ($('logoSizeWrap')) $('logoSizeWrap').style.display = 'block';
    setVal('pLogoSize', c.logoSize || 60);
    if ($('lbLogoSize')) $('lbLogoSize').textContent = c.logoSize || 60;
  } else {
    if ($('logoZoneContent')) $('logoZoneContent').innerHTML = '<div style="font-size:1.5rem;opacity:0.4;">🖼</div><div style="font-size:0.78rem;color:var(--ui-text-soft);">Click to upload logo</div><div class="logo-hint">PNG, JPG, SVG</div>';
    if ($('removeLogoBtn')) $('removeLogoBtn').style.display = 'none';
    if ($('logoSizeWrap')) $('logoSizeWrap').style.display = 'none';
  }
}

function renderDeliverySignature() {
  const block = $('docSignatureBlock');
  if (!block) return;
  document.querySelectorAll('.folio-prepared-sig,.folio-received-sig').forEach(el => el.remove());
  const d = getActiveDoc();
  if (!d || d.type !== 'delivery') return;
  migrateDoc(d);
  const cols = block.querySelectorAll('.sig-col');
  const prep = cols[0];
  const recv = cols[1];

  if (prep) {
    const line = prep.querySelector('.sig-line');
    const ui = document.createElement('div');
    ui.className = 'folio-sign-ui folio-prepared-sig';
    ui.innerHTML = `${d.dispatchSignature ? `<img class="folio-sig-img" src="${d.dispatchSignature}"><div class="folio-sig-date">Signed ${fmtDate(d.dispatchAt)}</div>` : ''}<div class="folio-sign-actions edit-only"><button class="btn btn-ghost btn-sm" onclick="openSignaturePad('prepared')">${d.dispatchSignature ? 'Re-sign Prepared By' : 'Sign Prepared By'}</button>${d.dispatchSignature ? `<button class="btn btn-danger btn-sm" onclick="clearDeliverySignature('prepared')">Clear</button>` : ''}</div>`;
    prep.insertBefore(ui, line || prep.children[1] || null);
  }

  if (recv) {
    const line = recv.querySelector('.sig-line');
    const ui = document.createElement('div');
    ui.className = 'folio-sign-ui folio-received-sig';
    ui.innerHTML = `${d.receivedSignature ? `<img class="folio-sig-img" src="${d.receivedSignature}"><div class="folio-sig-date">Signed ${fmtDate(d.receivedAt)}</div>` : ''}<input class="sig-name-input edit-only" value="${esc(d.receivedByName || '')}" placeholder="Name & designation…" oninput="getActiveDoc().receivedByName=this.value;persist();renderDeliverySignature()"><div class="sig-name-print print-only-text">${esc(d.receivedByName || '')}</div><div class="folio-sign-actions edit-only"><button class="btn btn-ghost btn-sm" onclick="openSignaturePad('received')">${d.receivedSignature ? 'Re-sign Received By' : 'Sign Received By'}</button>${d.receivedSignature ? `<button class="btn btn-danger btn-sm" onclick="clearDeliverySignature('received')">Clear</button>` : ''}</div>`;
    recv.insertBefore(ui, line || recv.children[1] || null);
  }
}
function openSignaturePad(target = 'received') {
  _signatureTarget = target;
  let modal = $('signatureModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'signatureModal';
    modal.className = 'modal-overlay open';
    modal.innerHTML = `<div class="modal-box"><h2 id="signatureModalTitle">Signature</h2><canvas id="signatureCanvas" style="width:100%;height:220px;background:#fff;border:1px dashed #999;border-radius:12px;touch-action:none"></canvas><div class="modal-actions"><button class="btn btn-ghost" onclick="closeSignaturePad()">Cancel</button><button class="btn btn-ghost" onclick="setupSignatureCanvas()">Clear</button><button class="btn btn-accent" onclick="saveSignaturePad()">Save Signature</button></div></div>`;
    document.body.appendChild(modal);
  }
  if ($('signatureModalTitle')) $('signatureModalTitle').textContent = target === 'prepared' ? 'Prepared By Signature' : 'Received By Signature';
  modal.classList.add('open');
  modal.classList.add('show');
  setTimeout(setupSignatureCanvas, 50);
}
function closeSignaturePad() { $('signatureModal')?.classList.remove('open'); $('signatureModal')?.classList.remove('show'); }
function setupSignatureCanvas() {
  const canvas = $('signatureCanvas');
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, rect.width * ratio);
  canvas.height = Math.max(1, rect.height * ratio);
  const ctx = canvas.getContext('2d');
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, rect.width, rect.height);
  ctx.strokeStyle = '#111';
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  let drawing = false;
  const pos = e => { const p = e.touches?.[0] || e.changedTouches?.[0] || e; const r = canvas.getBoundingClientRect(); return { x: p.clientX - r.left, y: p.clientY - r.top }; };
  const start = e => { e.preventDefault(); drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = e => { if (!drawing) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = e => { if (!drawing) return; e.preventDefault(); drawing = false; };
  canvas.onmousedown = start; canvas.onmousemove = move; canvas.onmouseup = end; canvas.onmouseleave = end;
  canvas.ontouchstart = start; canvas.ontouchmove = move; canvas.ontouchend = end; canvas.ontouchcancel = end;
}
function saveSignaturePad() {
  const d = getActiveDoc();
  const canvas = $('signatureCanvas');
  if (!d || !canvas) return;
  const img = canvas.toDataURL('image/png');
  if (_signatureTarget === 'prepared') { d.dispatchSignature = img; d.dispatchAt = today(); }
  else { d.receivedSignature = img; d.receivedAt = today(); }
  persist();
  closeSignaturePad();
  renderDeliverySignature();
}
function clearDeliverySignature(target = 'received') {
  const d = getActiveDoc();
  if (!d) return;
  if (target === 'prepared') { d.dispatchSignature = ''; d.dispatchAt = ''; }
  else { d.receivedSignature = ''; d.receivedAt = ''; }
  persist();
  renderDeliverySignature();
}
function renderPaidStamp() {
  document.getElementById('folio-paid-wrap')?.remove();
  const d = getActiveDoc();
  const totals = document.querySelector('.doc-totals');
  const block = document.querySelector('.doc-totals .totals-block');
  if (!d || d.type !== 'invoice' || !totals) return;
  const wrap = document.createElement('div');
  wrap.id = 'folio-paid-wrap';
  wrap.className = 'folio-paid-wrap';
  wrap.style.cssText = 'flex:1;display:flex;align-items:center;justify-content:center;';
  wrap.innerHTML = d.paidStamp ? `<div class="folio-paid-stamp">PAID<br><small>${fmtDate(d.paidStampDate)}</small></div><button class="btn btn-ghost btn-sm edit-only" onclick="getActiveDoc().paidStamp=false;persist();renderPaidStamp()">Undo</button>` : `<button class="btn btn-ghost btn-sm edit-only" onclick="getActiveDoc().paidStamp=true;getActiveDoc().paidStampDate=today();persist();renderPaidStamp()">Mark as Paid</button>`;
  totals.insertBefore(wrap, block || totals.firstChild);
}

function openCompanyDashboard() {
  let m = $('companyDashboardModal');
  if (!m) {
    m = document.createElement('div');
    m.id = 'companyDashboardModal';
    m.className = 'modal-overlay';
    m.innerHTML = '<div class="modal-box" style="max-width:980px;width:96vw"><h2>Company Management</h2><div id="companyDashboardBody"></div><div class="modal-actions"><button class="btn btn-ghost" onclick="closeModal(\'companyDashboardModal\')">Close</button><button class="btn btn-accent" onclick="saveCompanyDashboard()">Save Changes</button></div></div>';
    document.body.appendChild(m);
  }
  renderCompanyDashboard();
  openModal('companyDashboardModal');
}
function renderCompanyDashboard() {
  const body = $('companyDashboardBody');
  if (!body) return;
  const c = getCompany();
  body.innerHTML = `<div class="company-dashboard-grid"><div>${companies.map((co, i) => `<button class="btn ${i === activeCompanyIdx ? 'btn-accent' : 'btn-ghost'} btn-sm" style="width:100%;margin-bottom:6px" onclick="switchCompany(${i});renderCompanyDashboard()">${esc(co.name)}</button>`).join('')}<button class="btn btn-ghost btn-sm" onclick="openNewCompanyModal()">+ Add Company</button><button class="btn btn-danger btn-sm" onclick="deleteCurrentCompany();renderCompanyDashboard()">Delete Active</button></div><div><div class="modal-grid"><div class="modal-group modal-full"><label>Name</label><input id="dashName" value="${esc(c.name)}"></div><div class="modal-group"><label>Tagline</label><input id="dashTagline" value="${esc(c.tagline || '')}"></div><div class="modal-group"><label>Tax / Reg No</label><input id="dashTax" value="${esc(c.taxNo || '')}"></div><div class="modal-group"><label>Phone</label><input id="dashPhone" value="${esc(c.phone || '')}"></div><div class="modal-group"><label>Email</label><input id="dashEmail" value="${esc(c.email || '')}"></div><div class="modal-group"><label>Website</label><input id="dashWebsite" value="${esc(c.website || '')}"></div><div class="modal-group"><label>Currency</label><input id="dashCurrency" value="${esc(c.currency || '$')}"></div><div class="modal-group modal-full"><label>Address</label><textarea id="dashAddress">${esc(c.address || '')}</textarea></div><div class="modal-group"><label>Quote Next</label><input id="dashQSeq" type="number" value="${c.sequences.quotation.next}"></div><div class="modal-group"><label>Invoice Next</label><input id="dashISeq" type="number" value="${c.sequences.invoice.next}"></div><div class="modal-group"><label>Delivery Next</label><input id="dashDSeq" type="number" value="${c.sequences.delivery.next}"></div></div><button class="btn btn-ghost btn-sm" onclick="document.getElementById('dashLogoFile').click()">Upload Logo</button><input id="dashLogoFile" type="file" accept="image/*" style="display:none" onchange="dashboardLogoUpload(event)"><div style="margin-top:10px"><strong>Bank Accounts</strong><div id="dashBanks">${(c.bankAccounts || []).map((a, ai) => `<div class="company-bank-card"><input value="${esc(a.title)}" oninput="getCompany().bankAccounts[${ai}].title=this.value;persist();refreshBank()" placeholder="Title">${(a.fields || []).map((f, fi) => `<div class="company-bank-row"><input value="${esc(f.label)}" oninput="getCompany().bankAccounts[${ai}].fields[${fi}].label=this.value;persist();refreshBank()"><input value="${esc(f.value)}" oninput="getCompany().bankAccounts[${ai}].fields[${fi}].value=this.value;persist();refreshBank()"></div>`).join('')}<button class="btn btn-ghost btn-sm" onclick="getCompany().bankAccounts[${ai}].fields.push({label:'Label',value:''});renderCompanyDashboard();persist()">+ Field</button></div>`).join('')}</div><button class="btn btn-ghost btn-sm" onclick="addNewBankAccount();renderCompanyDashboard()">+ Bank Account</button></div></div></div>`;
}
function saveCompanyDashboard() {
  const c = getCompany();
  c.name = val('dashName'); c.tagline = val('dashTagline'); c.taxNo = val('dashTax'); c.phone = val('dashPhone'); c.email = val('dashEmail'); c.website = val('dashWebsite'); c.currency = val('dashCurrency') || '$'; c.address = val('dashAddress');
  c.sequences.quotation.next = Number(val('dashQSeq')) || 1;
  c.sequences.invoice.next = Number(val('dashISeq')) || 1;
  c.sequences.delivery.next = Number(val('dashDSeq')) || 1;
  syncBasicToCompanyFields(c);
  refreshCompanySelector(); loadCompanyIntoUI(activeCompanyIdx); refreshHeader(); refreshBank(); persist(); showToast('Company settings saved');
}
function dashboardLogoUpload(e) { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = ev => { getCompany().logo = ev.target.result; refreshHeader(); updateLogoZone(); persist(); renderCompanyDashboard(); }; r.readAsDataURL(file); }

async function downloadPDF() {
  const d = getActiveDoc();
  if (!d) return showToast('Open a document first', '#ff6b6b');
  syncPrintFields();
  renderDeliverySignature();
  renderPaidStamp();
  const el = $('printArea');
  const opt = { margin: 0, filename: `${safeFileName(d.number)}.pdf`, image: { type: 'jpeg', quality: .98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'pt', format: 'a4', orientation: 'portrait' } };
  document.body.classList.add('printing');
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try { await html2pdf().set(opt).from(el).save(); }
  finally { document.body.classList.remove('printing'); }
}
function exportData() { const blob = new Blob([JSON.stringify(serializeState(), null, 2)], { type: 'application/json' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `folio-backup-${today()}.json`; a.click(); URL.revokeObjectURL(a.href); }
function importData(e) { const file = e.target.files?.[0]; if (!file) return; const r = new FileReader(); r.onload = ev => { try { const payload = JSON.parse(ev.target.result); if (!confirm('Import backup and replace current data?')) return; applyState(payload); persist(); renderAfterRemoteState(); showToast('Backup imported'); } catch { showToast('Could not import backup', '#ff6b6b'); } finally { e.target.value = ''; } }; r.readAsText(file); }
function openDesignInspector() { document.body.classList.add('design-open'); document.body.classList.remove('docs-open'); showTab('tab-design'); loadDesignControls(); }
function initMobileDock() { if ($('folio-mobile-dock')) return; const dock = document.createElement('div'); dock.id = 'folio-mobile-dock'; dock.className = 'folio-mobile-dock'; dock.innerHTML = '<button onclick="document.body.classList.toggle(\'docs-open\');document.body.classList.remove(\'design-open\')"><span>☰</span>Docs</button><button onclick="openDesignInspector()"><span>⚙</span>Design</button><button onclick="newDocument()"><span>＋</span>New</button><button onclick="openCompanyDashboard()"><span>🏢</span>Company</button>'; document.body.appendChild(dock); }

window.addEventListener('DOMContentLoaded', async () => {
  injectCoreStyles();
  await loadState();
  normalizeAll();
  startRealtimeSync();
  refreshCompanySelector();
  loadCompanyIntoUI(activeCompanyIdx);
  applyTheme();
  loadDesignControls();
  renderDocList();
  if (activeDocId && documents.some(d => d.id === activeDocId)) openDoc(activeDocId, false);
  else closeEditor();
  initMobileDock();
});
